import React, { useState } from "react";
import GuideLayout from "../../components/layout/GuideLayout";
import Section from "../../components/layout/Section";
import Subheader from "../../components/layout/Subheader";
import {
  Typography,
  Link,
  Box,
  TextField,
  Checkbox,
  FormControlLabel
} from "@mui/material";

import ProductRecommendationQuiz from "../../components/ProductRecommendationQuiz";
import CodeHighlight from "../../components/layout/CodeHighlight";
import NCMathTable from "../../components/NCMathTable";


const sections = [
  { id: "intro", title: "top" },
  {
    id: "where-to-buy-nc",
    title: "Where to Buy Neocash",
  },
  {
    id: "step-by-step",
    title: "Buying On-site: Step By Step",
  },
  {
    id: "quiz",
    title: "Fun Quiz - How Much Should You Buy?"
  }
];

const BuyingNC = () => {
  const [amount, setAmount] = useState("");
  const [price, setPrice] = useState("");
  const [showBoxCalcs, setShowBoxCalcs] = useState(false);

  return (
    <GuideLayout sections={sections}>
      <Section id="intro" title="How to Buy Neocash">
        <Typography
          variant="caption"
          color="secondary"
          sx={{ mt: -1, mb: 2, display: "block" }}
        >
          Last updated: September 2025
        </Typography>
        <Typography paragraph>
          If you want to participate in NC customization and trading, you pretty much have to buy NC. Let's go over the different ways to buy NC and what will give you the most bang for your buck. (All prices on this page are USD.)
        </Typography>

        <Subheader title={"Disclaimer"} />
        <Typography paragraph>
          Since we're dealing with real money and financial decisions, here's our disclaimer that purchases are made at your own discretion. This guide is here to give basic information on NC packages and how you might want to consider one over another based on your personal goals in the game. Everyone's situation will be different, so we encourage everyone to make financial decisions based on their own circumstances. While we will try our best to update our guide, TNT may also change prices or bundle contents at any time, so please double check before making a purchase.
        </Typography>
      </Section>

      <Section id="where-to-buy-nc" title="Where to Buy Neocash">
        <Typography paragraph>
          There are two main ways that players buy Neocash nowadays: direct from site and third party nc cards.
        </Typography>

        <Subheader title={"NC Cards"} />
        <Typography paragraph>
          You can purchase NC cards from a third party seller. When you buy an NC card, you will receive a code that you can <Link href="https://secure.nc.neopets.com/redeemnc">redeem onsite</Link> for 750nc + 2 boxes. These third party sellers are often in a country where the currency exchange rate allows them to comfortably sell cards at a competitive rate compared to the NC bundles on site. Originally, this was a very popular way to buy NC because it was cheaper and provided boxes, however, TNT introduced multiple price hikes for NC cards, meaning it was no longer cheaper than buying direct. In March 2025, they also introduced new incentives for buying direct by including a couple boxes and trendsetter caps based on the bundle amount.
        </Typography>

        <Typography paragraph>
          Buying NC cards isn't as popular as it used to be, but it is still an option to purchase NC. Some people still buy cards because they give more boxes than buying direct and some buy cards as an easy way to gift a friend. Many card sellers have closed shop since the price increase, but there are still around if you want to ask around and search!
        </Typography>

        <Subheader title={"Direct From Site"} />
        <Typography paragraph>
          <Link href="https://nc.neopets.com/get-neocash">Buying NC direct from the site</Link> is how most traders purchase nowadays. You can purchase NC in increments of $5, $10, $20, $50, or $100. The typical rate for NC is $1 = 100NC, but with different bundles, "bonus" NC is offered to make it more enticing. Prior to March 2025, buying NC on site did not give any items, but now you may receive boxes and/or trendsetter caps depending on the amount you purchase.
        </Typography>
      </Section>

      <Section id="trendsetter" title="Trendsetter Caps">
        <Typography paragraph>
          A Trendsetter Mystery Capsule is a gift with purchase for the $20, $50, and $100 NC bundles. These capsules will rotate every so often, but there is no known schedule. Typically, TNT will announce in the news if there will be a new rotation soon. So far, they have been named Trendsetter, but it is unclear if TNT will always use this naming convention. Therefore, we always that you check what caps you will get on the review purchase page, and check the item pool on a tool like JellyNeo to see what items are in the cap.
        </Typography>

        <Subheader title={"Are they worth anything?"} />

        <Typography paragraph>
          It will depend if the items in the pool are well received or not. Generally, trendsetter items are easier to trade when it's towards the beginning of the rotation and may be worth more when there's not many of the items around. Over time, the values may drop as more people obtain the items through regular NC purchases. Historically speaking, at the time of this article, there has been 2 trendsetter cap rotations so far, and unopened caps have ranged from 1-3 (usually higher at the start). However, the best answer is to check trade reports or simply ask around the community!
        </Typography>
      </Section>

      <Section id="comparison" title="Value Comparisons">
        <Typography paragraph>
          The table below compares NC bundles as well as a row to compare NC cards. We will not be linking to any specific seller, but you may input a price and neocash amount and we will do the other calculations for you!
        </Typography>

        <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>

          <TextField
            type="text"
            label="NC Amount"
            value={amount}
            onChange={(e) => {
              const raw = e.target.value;
              const regex = /^\d*\.?\d{0,2}$/; // allow only up to 2 decimals
              if (raw === "" || regex.test(raw)) {
                setAmount(raw); // keep as string
              }
            }}
            onBlur={() => {
              if (amount !== "" && !isNaN(amount)) {
                setAmount(parseFloat(amount).toFixed(2)); // auto-format
              }
            }}
            inputProps={{
              inputMode: "decimal",
              pattern: "^[0-9]*\\.?[0-9]{0,2}$",
            }}
            sx={{
              "& input": { MozAppearance: "textfield" },
              "& input::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
              "& input::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
              width: "150px",
              mr: 2,
            }}
          />

          <TextField
            type="text"
            label="Price (USD)"
            value={price}
            onChange={(e) => {
              const raw = e.target.value;
              const regex = /^\d*\.?\d{0,2}$/;
              if (raw === "" || regex.test(raw)) {
                setPrice(raw);
              }
            }}
            onBlur={() => {
              if (price !== "" && !isNaN(price)) {
                setPrice(parseFloat(price).toFixed(2));
              }
            }}
            inputProps={{
              inputMode: "decimal",
              pattern: "^[0-9]*\\.?[0-9]{0,2}$",
            }}
            sx={{
              "& input": { MozAppearance: "textfield" },
              "& input::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
              "& input::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
              width: "150px",
              mr: 2,
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={showBoxCalcs}
                onChange={(e) => setShowBoxCalcs(e.target.checked)}
              />
            }
            label="Display Box Math Calculations"
          />
        </Box>
        <Box sx={{ pb: 4 }}>
          <NCMathTable amount={amount} price={price} showBoxCalcs={showBoxCalcs} />
        </Box>

        <Typography paragraph>
          If boxes really matter to you, you can check the option for box calculations and see the comparison between buying an equivalent number of NC cards. The GBC column assumes the regular price of a GBC (200NC) and 2 boxes each, as that's the guaranteed amount. Likewise, the BFGBC column uses the BFGBC price (100NC) and accounts for 4 boxes each.
        </Typography>

        <Subheader title={"$20 Multiple Times vs $100 Math"} />
        <Typography paragraph>
          As you can see, if you prefer to have more trendsetter caps, you can technically buy the the $20 bundle 5 times (5 caps) and get more caps than the $100 bundle (3 caps). Buying the $20 bundle 5 times also means 20 boxes, which is 12 more than getting the $100 bundle that comes with 8. In theory, you would be spending the same amount, but getting more in items. This sounds like a great hack, however, it is not recommended due to the value of bonus NC from the $100 bundle.
        </Typography>

        <Typography paragraph>
          While you'd get 2 more trendsetter caps and 12 more boxes, you'd be getting 11,000NC compared to 14,000NC, which 3,000NC is a huge difference to miss out on. To put it in perspective, with 3,000NC, you could buy 15 regular GBCs or 30 BFGBCs, meaning you could have easily bought back the boxes you "missed out" on + trade for trendsetter caps and still have some leftover.
        </Typography>


        <Subheader title={"What's the Best Bundle?"} />
        <Typography paragraph>
          The $100 bundle is the best value in terms of NC per dollar, but <b>the best bundle will always be the one that aligns with your goals</b>. For example, if you are only looking to make a one time purchase for a 200NC item and nothing more, then you might want to consider the lower bundles instead. We encourage everyone to spend responsibly!
        </Typography>
      </Section>

      <Section id="step-by-step" title="Buying On-site: Step By Step">
        <Typography paragraph>
          To purchase NC on site, you must get to the <Link href="https://nc.neopets.com/get-neocash">NC page</Link>. Feel free to click our direct link, or if you want to start from the site, start by selecting Shop on the navigation, and switch to the NC tab to get to the Buy NC button.
        </Typography>
        <Box sx={{ textAlign: "center" }}>
          <img src={require('../../assets/buying-nc/step1_nav.png')} alt="shop navigation" style={{ maxWidth: "80%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <img src={require('../../assets/buying-nc/step2_nc_tab.png')} alt="nc tab" style={{
            width: '200px', maxWidth: "80%", borderRadius: 8, marginTop: 16, padding: '10px'
          }} />
          <img src={require('../../assets/buying-nc/step3_buy.png')} alt="buy nc button" style={{ width: '200px', maxWidth: "80%", borderRadius: 8, marginTop: 16, padding: '10px' }} />

        </Box>
        <Typography paragraph>
          Once you get to the Buy NC page, select the bundle you want to get to the review payment page. If you switch between logging into your side accounts and main account, be sure to check the username at the top right corner! Purchases are not refundable and not transferrable, so you don't want to accidentally purchase on the wrong account!
        </Typography>

        <Box sx={{ textAlign: "center" }}>
          <img src={require('../../assets/buying-nc/step4_buy_page.png')} alt="buy nc button" style={{ maxWidth: "80%", borderRadius: 8, marginTop: 15, marginBottom: 15 }} />
        </Box>
        <Typography paragraph>
          Review that you selected the right amount of NC to buy and enter your payment information. Once you have confirmed all the right information, press the buy button exactly once. <b>DO NOT</b> press it multiple times or else you risk multiple transactions.
        </Typography>

        <Box sx={{ textAlign: "center" }}>
          <img src={require('../../assets/buying-nc/step5_payment.png')} alt="buy nc button" style={{ maxWidth: "80%", borderRadius: 8, marginTop: 15, marginBottom: 15 }} />
        </Box>

        <Typography paragraph>
          In most cases, your NC should show up almost immediately. If not, give it a while. Some users have said it's taken anywhere from a few extra minutes to (unfortunately) a few hours. If your NC has not shown up, check your method of payment - did you get charged? If your NC is still not available, it is advised to send a ticket for further customer support!
        </Typography>

        <Typography paragraph>
          It is important to note that chargebacks are <b>not</b> recommended. Like other in games with their transactions, if you initiate a chargeback, your account may end up frozen. It can certainly be frustrating to wait for your NC, but being patient and contacting customer support is the way to go!
        </Typography>
      </Section>


      <Section id="quiz" title="Fun Quiz - How Much Should You Buy?">
        We've made a lil fun quiz to recommend you a NC bundle based on your goals. Try it out!
        <CodeHighlight text={"Disclaimer again... This quiz is for fun and it should not be what makes your financial decisions."} />
        <ProductRecommendationQuiz />
      </Section>

    </GuideLayout>
  );
};

export default BuyingNC;
